import { useEffect, useState } from "react";
import axios from "axios";
import Header from "../../../components/Header";
import { motion } from "framer-motion";
import { MapPin, CalendarDays } from "lucide-react";

function MyReservations() {
  const [reservations, setReservations] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchReservations = async () => {
      try {
        const res = await axios.get(
          "https://api.komorotel.com/api/reservation/user",
          { withCredentials: true }
        );
        setReservations(res.data);
      } catch (err) {
        console.log(err);
      } finally {
        setLoading(false);
      }
    };

    fetchReservations();
  }, []);

  const handleCancelRequest = async (id) => {
    try {
      await axios.post(
        `https://api.komorotel.com/api/reservation/${id}/cancellation-request`,
        { reservationId: id },
        { withCredentials: true }
      );

      setReservations((prev) =>
        prev.map((r) =>
          r._id === id ? { ...r, status: "pending_cancel" } : r
        )
      );
    } catch (err) {
      alert(err.response?.data?.message || "Erreur");
    }
  };

  const getStatusUI = (status) => {
  switch (status) {
    case "confirmed":
      return {
        text: "Confirmée",
        color: "bg-green-500"
      };
    case "pending_cancel":
      return {
        text: "Annulation en cours",
        color: "bg-yellow-500"
      };
    case "cancelled":
      return {
        text: "Annulée",
        color: "bg-red-500"
      };
    default:
      return {
        text: "Inconnue",
        color: "bg-gray-400"
      };
  }
};

  return (
    <>
      <Header />

      <div className="pt-32 pb-20 bg-gray-50 min-h-screen">

        <div className="max-w-5xl mx-auto px-4">

          {/* HEADER */}
          <div className="mb-10">
            <h1 className="text-3xl font-semibold text-gray-900">
              Mes réservations
            </h1>
            <p className="text-gray-500 mt-1">
              Consultez et gérez vos séjours
            </p>
          </div>

          {/* LOADING */}
          {loading && (
            <p className="text-gray-500">Chargement...</p>
          )}

          {/* EMPTY */}
          {!loading && reservations.length === 0 && (
            <div className="bg-white rounded-2xl p-8 text-center border">
              <p className="text-gray-500">
                Aucune réservation pour le moment.
              </p>
            </div>
          )}

          {/* LIST */}
          <div className="flex flex-col gap-6">

            {reservations.map((r, i) => {
              const status = getStatusUI(r.status);


              const canCancel = r.status === "confirmed";

              return (
                <motion.div
                  key={r._id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.04 }}
                  className="
                    group bg-white rounded-2xl border
                    overflow-hidden
                    hover:shadow-md transition
                  "
                >

                  <div className="flex flex-col md:flex-row">

                    {/* IMAGE */}
                    <div className="relative md:w-[240px] h-[180px] overflow-hidden">

                        <img
                            src={r.roomId?.photos?.[0]}
                            className="
                            w-full h-full object-cover
                            transition-transform duration-500
                            group-hover:scale-105
                            "
                        />

                        

                        </div>

                    {/* CONTENT */}
                    <div className="flex-1 p-5 flex flex-col justify-between">

                      {/* TOP */}
                      <div>

                        {/* HOTEL */}
                        <p className="text-sm text-gray-500 flex items-center gap-1">
                          <MapPin size={14} />
                          {r.hotelId?.name} · {r.hotelId?.city}
                        </p>

                        {/* ROOM */}
                        <h2 className="text-lg font-semibold text-gray-900 mt-1">
                          {r.roomId?.title}
                        </h2>

                        {/* DATES */}
                        <p className="text-sm text-gray-500 mt-2 flex items-center gap-1">
                          <CalendarDays size={14} />
                          {new Date(r.checkInDate).toLocaleDateString()} →{" "}
                          {new Date(r.checkOutDate).toLocaleDateString()}
                        </p>

                        <p className="text-xs text-gray-400 mt-2">
                          Réf : {r.reservationId}
                        </p>

                      </div>

                      {/* BOTTOM */}
                      <div className="flex items-center justify-between mt-5">

                        {/* STATUS */}
                        <span className="flex items-center gap-2 text-sm font-medium text-gray-900">
                        <span className={`w-2 h-2 rounded-full ${status.color}`}></span>
                        {status.text}
                        </span>

                        {/* PRICE + ACTION */}
                        <div className="text-right">

                          <p className="text-lg font-semibold text-gray-900">
                            {r.totalPrice}€
                          </p>

                          {canCancel && (
                            <button
                              onClick={() => handleCancelRequest(r._id)}
                              className="text-sm text-red-500 hover:text-red-600 mt-1"
                            >
                              Demander l’annulation
                            </button>
                          )}

                        </div>

                      </div>

                    </div>

                  </div>

                </motion.div>
              );
            })}

          </div>

        </div>

      </div>
    </>
  );
}

export default MyReservations;